import React from 'react';
import { Link } from 'react-router-dom';
import { Heart, ArrowRight, Award, GraduationCap, Play } from 'lucide-react';
import ShareButtons from '../components/ShareButtons';
import { ImpactCounter, EventCalendar } from '../components/SponsorExtras';
import { givebutterUrl, hopeFloatsVideoUrl, sponsorHeroImage } from '../data/mock';

const HOPE_FLOATS_URL = 'https://www.hopefloats.foundation';
const SHARE_URL = 'https://littlesplashswimpro.com/sponsor-a-lesson';

export default function SponsorALesson() {
  return (
    <main className="bg-splash-cream">
      {/* Hero */}
      <section className="pt-8 md:pt-12 pb-16" data-testid="sponsor-hero">
        <div className="max-w-6xl mx-auto px-6">
          <div className="relative rounded-[2rem] overflow-hidden shadow-xl h-[260px] sm:h-[360px] lg:h-[460px]">
            <img
              src={sponsorHeroImage}
              alt="Coach and smiling swimmers in the Little Splash indoor pool"
              fetchpriority="high"
              decoding="async"
              data-testid="sponsor-hero-image"
              className="w-full h-full object-cover"
              style={{ objectPosition: 'center 22%' }}
            />
            <div className="absolute top-4 left-4 rounded-full bg-splash-coral text-white text-[10px] font-semibold px-3 py-1 uppercase tracking-widest shadow-sm">
              Partnership announced 9/1/2026
            </div>
          </div>
          <div className="mt-8 text-splash-green/60 text-xs uppercase tracking-widest font-semibold" data-testid="sponsor-local-line">
            Local funds &middot; Local kids &middot; Des Moines Metro
          </div>
          <div className="mt-4 max-w-3xl">
            <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl text-splash-green leading-[1.05]">
              Give a child the gift of<br className="hidden sm:block" /> water safety.
            </h1>
            <p className="mt-6 text-splash-green/80 text-base md:text-lg leading-relaxed max-w-2xl">
              Little Splash Swim Program is an official swim school partner of the{' '}
              <strong className="text-splash-green">Hope Floats Foundation</strong>. Your donation
              funds scholarships that put lifesaving swim lessons within reach of Des Moines-area
              children whose families face financial barriers.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href={givebutterUrl}
                target="_blank"
                rel="noreferrer"
                data-testid="sponsor-hero-donate"
                className="inline-flex items-center gap-2 rounded-full bg-splash-coral hover:bg-splash-coralDark text-white px-7 py-3.5 font-semibold btn-transition"
              >
                <Heart className="w-4 h-4" strokeWidth={2.5} /> Donate Now
              </a>
              <a
                href={HOPE_FLOATS_URL}
                target="_blank"
                rel="noreferrer"
                data-testid="sponsor-hero-learn"
                className="inline-flex items-center gap-2 rounded-full bg-white border border-splash-green/15 text-splash-green px-6 py-3.5 font-semibold btn-transition"
              >
                Learn About Hope Floats <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      <ImpactCounter />

      {/* Hope Floats video band */}
      <section className="py-14 md:py-16 bg-splash-creamDark">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-8">
            <div className="uppercase tracking-widest text-splash-coral text-xs font-semibold mb-3 inline-flex items-center gap-2 justify-center">
              <Play className="w-3.5 h-3.5" /> Watch — Hope Floats Helps Families
            </div>
            <h2 className="font-display font-bold text-3xl md:text-4xl text-splash-green leading-tight">
              Real families. Real impact.
            </h2>
          </div>
          <div className="relative rounded-3xl overflow-hidden border border-splash-sand bg-black aspect-video shadow-lg">
            <video
              src={hopeFloatsVideoUrl}
              autoPlay
              muted
              loop
              playsInline
              controls
              preload="metadata"
              className="w-full h-full object-cover"
              data-testid="sponsor-hope-floats-video"
            />
          </div>
          <p className="mt-4 text-center text-splash-green/70 text-sm max-w-2xl mx-auto">
            A quick look at how Hope Floats scholarships change lives &mdash; the same mission we&rsquo;re proud
            to now deliver locally in Des Moines through Little Splash.
          </p>
        </div>
      </section>

      {/* Impact stats */}
      <section className="bg-splash-green text-splash-cream py-16 md:py-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-10">
            <div className="uppercase tracking-widest text-splash-coral text-xs font-semibold mb-3">Why it matters</div>
            <h2 className="font-display font-bold text-3xl md:text-5xl text-white">The stakes couldn&rsquo;t be higher.</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { stat: '13', label: 'School buses of children', body: 'The equivalent of thirteen school buses full of children die each year in the United States due to drowning.' },
              { stat: '88%', label: 'Lower drowning risk', body: 'Studies at the National Institute of Health show that swim lessons can reduce the risk of drowning by 88%.' },
              { stat: '300+', label: 'Hope Floats swim schools', body: 'Hope Floats partners with local communities to offer need-based scholarships at 300+ swim schools in 40 states.' },
            ].map((s) => (
              <div key={s.label} className="rounded-3xl bg-white/5 border border-white/10 backdrop-blur p-8">
                <div className="font-display font-bold text-5xl md:text-6xl text-splash-coral">{s.stat}</div>
                <div className="mt-2 uppercase tracking-widest text-[11px] font-semibold text-splash-cream/70">{s.label}</div>
                <p className="mt-4 text-splash-cream/85 leading-relaxed text-sm">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Story + quotes */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-6">
          <div className="uppercase tracking-widest text-splash-coral text-xs font-semibold mb-3">Our partnership</div>
          <h2 className="font-display font-bold text-3xl md:text-5xl text-splash-green leading-tight">
            Joining forces to remove barriers to swim lessons.
          </h2>
          <p className="mt-6 text-splash-green/80 leading-relaxed">
            Through this partnership, Little Splash Swim Program will help make quality swim lessons
            more accessible to children and families in the Des Moines Metro who may otherwise face
            financial barriers to participation. As a locally owned swim program, Little Splash is
            proud to bring the Hope Floats mission directly into the community it serves.
          </p>

          <figure className="mt-10 rounded-3xl bg-splash-cream border border-splash-sand p-8 md:p-10">
            <blockquote className="font-quote text-splash-green text-xl md:text-2xl leading-snug italic">
              &ldquo;At Little Splash, we believe every child deserves the opportunity to learn the
              skills that can help keep them safe around water, regardless of their family&rsquo;s
              financial circumstances. As a small, locally owned swim program, being able to pay it
              forward and provide opportunities for children who may otherwise not have access to
              swim lessons is incredibly meaningful to us.&rdquo;
            </blockquote>
            <figcaption className="mt-5 text-sm text-splash-green/70">
              &mdash; <strong className="text-splash-green">Abrie Freeman</strong>, Founder &amp;
              Program Director, Little Splash Swim Program
            </figcaption>
          </figure>

          <figure className="mt-8 rounded-3xl bg-white border border-splash-sand p-8 md:p-10">
            <blockquote className="font-quote text-splash-green text-lg md:text-xl leading-snug italic">
              &ldquo;There&rsquo;s a huge need for swim lessons, and the kids that need them the
              most &mdash; lower socio-economic level kids &mdash; they&rsquo;re not getting them.
              We are saving kids, but there are more we need to reach, and we have the ability and
              resources to do it.&rdquo;
            </blockquote>
            <figcaption className="mt-5 text-sm text-splash-green/70">
              &mdash; <strong className="text-splash-green">Cindy Tonnesen</strong>, Founder, Hope
              Floats Foundation
            </figcaption>
          </figure>
        </div>
      </section>

      {/* Donate CTA */}
      <section id="donate" className="pb-20">
        <div className="max-w-4xl mx-auto px-6">
          <div className="rounded-3xl bg-gradient-to-br from-splash-coral to-splash-coralDark text-white p-8 md:p-12 shadow-xl">
            <div className="grid md:grid-cols-[1fr_auto] items-center gap-8">
              <div>
                <div className="uppercase tracking-widest text-white/90 text-xs font-semibold mb-3 flex items-center gap-2">
                  <Heart className="w-3.5 h-3.5" /> Donate Now
                </div>
                <h2 className="font-display font-bold text-3xl md:text-4xl leading-tight">
                  Sponsor a lesson. Change a life.
                </h2>
                <p className="mt-4 text-white/90 leading-relaxed max-w-lg">
                  100% of every gift goes to the Hope Floats Foundation and returns to Des Moines
                  as scholarships for Little Splash swim lessons. Every $40 sponsors one child
                  for a group class.
                </p>
                <p className="mt-3 text-white/70 text-sm">
                  Your donation is processed securely by Givebutter on behalf of the Hope Floats
                  Foundation (a registered 501(c)(3) nonprofit). You&rsquo;ll receive a tax receipt
                  by email.
                </p>
              </div>
              <div className="flex flex-col items-stretch md:items-end gap-3">
                <a
                  href={givebutterUrl}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="sponsor-donate-cta"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-white text-splash-coralDark hover:bg-splash-cream px-8 py-4 text-base font-bold btn-transition shadow-md"
                  aria-label="Donate to Hope Floats Foundation via Givebutter"
                >
                  <Heart className="w-4 h-4 shrink-0" strokeWidth={2.5} />
                  <span>Donate to Hope Floats</span>
                </a>
                <div className="text-center md:text-right text-xs text-white/80">
                  Suggested: $10 · $20 · $30 · $70 · $100 · $200 · $1000
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <EventCalendar />

      {/* Share */}
      <section className="pb-14" data-testid="sponsor-share-section">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="uppercase tracking-widest text-splash-coral text-xs font-semibold mb-3">Spread the word</div>
          <h2 className="font-display font-bold text-2xl md:text-3xl text-splash-green">Can&rsquo;t give today? Share instead.</h2>
          <p className="mt-2 mb-6 text-splash-green/75 text-sm">Every share helps another Des Moines child get in the water safely.</p>
          <div className="flex justify-center">
            <ShareButtons url={SHARE_URL} text="Help a Des Moines child learn to swim — sponsor a lesson with Little Splash & Hope Floats." />
          </div>
        </div>
      </section>

      {/* About cards */}
      <section className="pb-10 md:pb-12">
        <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-2 gap-6">
          <article className="rounded-3xl bg-white border border-splash-sand p-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-11 h-11 rounded-full bg-splash-teal/15 text-splash-teal grid place-items-center">
                <GraduationCap className="w-5 h-5" />
              </div>
              <h3 className="font-display font-bold text-2xl text-splash-green">About Little Splash</h3>
            </div>
            <p className="text-splash-green/80 leading-relaxed text-sm md:text-base">
              Little Splash Swim Program is a locally owned, play-based swim program serving
              children in the Des Moines Metro. Founded with the belief that children can learn to
              swim without fear-based methods, we focus on small class sizes, child-centered
              instruction, water safety, skill development, and building confidence in the water.
            </p>
            <Link
              to="/about-us"
              className="inline-flex items-center gap-2 mt-5 text-splash-teal hover:text-splash-tealDark font-semibold text-sm"
            >
              Meet the team <ArrowRight className="w-4 h-4" />
            </Link>
          </article>

          <article className="rounded-3xl bg-white border border-splash-sand p-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-11 h-11 rounded-full bg-splash-coral/15 text-splash-coralDark grid place-items-center">
                <Award className="w-5 h-5" />
              </div>
              <h3 className="font-display font-bold text-2xl text-splash-green">About Hope Floats Foundation</h3>
            </div>
            <p className="text-splash-green/80 leading-relaxed text-sm md:text-base">
              Hope Floats Foundation is a nonprofit founded in 2016 committed to impacting the
              statistics on worldwide drowning. The Hope Floats model is a simple, sustainable way
              for swim schools to provide scholarships for high-quality swim lessons in their
              community through a &ldquo;local funds for local kids&rdquo; model.
            </p>
            <a
              href={HOPE_FLOATS_URL}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 mt-5 text-splash-teal hover:text-splash-tealDark font-semibold text-sm"
            >
              hopefloats.foundation <ArrowRight className="w-4 h-4" />
            </a>
          </article>
        </div>
      </section>
    </main>
  );
}
